import { FormattedMessage } from 'react-intl';
import ContentLoader from '@rio-cloud/rio-uikit/ContentLoader';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';

import { STATS_WIDGET_PANEL_HEIGHT } from './StatsWidgetsContainer';

export type CustomStatsWidgetComponentProps = {
    /** ClassName configuration in order to get a responsive panel */
    responsiveGridConfig?: string;
    /** Show loading spinner instead of the whole component */
    isLoading: boolean;
    /** Total number of elements with status Critical */
    totalCriticalComponents: number;
    /** Total number of elements with status Warning */
    totalWarningComponents: number;
    /** Total number of elements with status Information*/
    totalInformationComponents?: number;
    /** Total number of elements with status Other*/
    totalOtherComponents?: number;
    /** Title of the Widget */
    customStatsWidgetTitle: string;
    /** Legend of total elements with status Critical */
    totalCriticalElementsLegend: string;
    /** Legend of total elements with status Warning */
    totalWarningElementsLegend: string;
    /** Legend of total elements with status Information */
    totalInformationElementsLegend?: string;
    /** Legend of total elements with status Other */
    totalOtherElementsLegend?: string;
    /** Legend when there is no warning/critical elements */
    allGoodComponentLegend: string;
    /** Tooltip message **/
    tooltipLegend?: string;
};

const StatsSkeletonScreen = () => (
    <div
        data-testid="custom-stats-widget-skeleton-screen"
        className="display-flex flex-column justify-content-center align-items-center margin-top-20"
    >
        <ContentLoader height={40} width={50} />
        <div className="margin-top-10" />
        <ContentLoader height={50} width={150} />
    </div>
);

const CustomStatsWidget = ({
    totalCriticalComponents,
    totalCriticalElementsLegend,
    totalWarningComponents,
    totalWarningElementsLegend,
    totalInformationComponents,
    totalInformationElementsLegend,
    totalOtherComponents,
    totalOtherElementsLegend,
}: CustomStatsWidgetComponentProps) => (
    <>
        {totalCriticalComponents > 0 && (
            <div className="margin-top-10 text-center" data-testid="custom-stats-widget-levels-item">
                <span className="uikit-icon icon-error-sign text-color-danger text-size-16" />
                <span className="text-bold margin-left-5">{totalCriticalComponents} </span>
                <FormattedMessage id={totalCriticalElementsLegend} />
            </div>
        )}
        {totalWarningComponents > 0 && (
            <div className="margin-top-10 text-center" data-testid="custom-stats-widget-levels-item">
                <span className="uikit-icon icon-warning-sign text-color-warning text-size-16" />
                <span className="text-bold margin-left-5">{totalWarningComponents} </span>

                <FormattedMessage id={totalWarningElementsLegend} />
            </div>
        )}
        {totalInformationComponents !== undefined && totalInformationComponents > 0 && (
            <div className="margin-top-10 text-center" data-testid="custom-stats-widget-levels-item">
                <span className="uikit-icon icon-info-sign text-color-info text-size-16" />
                <span className="text-bold margin-left-5">{totalInformationComponents} </span>

                <FormattedMessage id={totalInformationElementsLegend} />
            </div>
        )}
        {totalOtherComponents !== undefined && totalOtherComponents > 0 && (
            <div className="margin-top-10 text-center" data-testid="custom-stats-widget-levels-item">
                <span className="uikit-icon icon-ongoing text-color-gray text-size-16" />
                <span className="text-bold margin-left-5">{totalOtherComponents} </span>

                <FormattedMessage id={totalOtherElementsLegend} />
            </div>
        )}
    </>
);

const CustomStatsWidgetComponent = (props: CustomStatsWidgetComponentProps) => {
    const {
        responsiveGridConfig,
        isLoading,
        totalCriticalComponents,
        totalWarningComponents,
        customStatsWidgetTitle,
        allGoodComponentLegend,
        totalInformationComponents,
        totalOtherComponents,
        tooltipLegend,
    } = props;
    const totalComponents =
        totalCriticalComponents +
        totalWarningComponents +
        (totalInformationComponents !== undefined ? totalInformationComponents : 0) +
        (totalOtherComponents !== undefined ? totalOtherComponents : 0);
    return (
        <div
            className={`panel panel-default panel-body display-flex flex-column ${responsiveGridConfig}`}
            style={{ height: STATS_WIDGET_PANEL_HEIGHT }}
        >
            <h4 className="text-center text-bold margin-top-0 margin-bottom-0">
                <FormattedMessage id={customStatsWidgetTitle} />
                {tooltipLegend && (
                    <OverlayTrigger
                        placement={Tooltip.RIGHT}
                        overlay={
                            <Tooltip allowOnTouch textAlignment="left" width={200}>
                                <FormattedMessage id={tooltipLegend} />
                            </Tooltip>
                        }
                    >
                        <span
                            data-testid="tooltipInfoIcon"
                            className="uikit-icon icon-info-sign text-size-16 margin-left-10"
                            style={{ marginTop: 2 }}
                        />
                    </OverlayTrigger>
                )}
            </h4>
            <div className="display-flex justify-content-center">
                {isLoading ? (
                    <StatsSkeletonScreen />
                ) : (
                    <div>
                        <div className="text-center">
                            <h3>{totalComponents}</h3>
                        </div>
                        <div className="margin-top-10">
                            {totalComponents > 0 ? (
                                <CustomStatsWidget {...props} />
                            ) : (
                                <div className="margin-top-20 text-center">
                                    <FormattedMessage id={allGoodComponentLegend} />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomStatsWidgetComponent;
